import { useEntityRow } from './entity-store';
import type { EntityKind, ResolvedEntity } from '@/lib/canvas/types/entity';

// Re-export so existing consumers (tests, ConnectedEntityProxyNode) don't
// need to update their import paths. The canonical definition lives in
// lib/canvas/types/entity to keep this lib module from reaching into
// features/canvas.
export type { ResolvedEntity };

/**
 * Look up an entity by id (and optionally a kind hint) across all
 * BaseEntityRow-shaped Maps in `entityStore`.
 *
 * Returns null when:
 * - id is null/undefined (virtual entity-proxies have entityId=null)
 * - id is not present in any BaseEntityRow Map
 * - the resolved row's kind doesn't match the explicit `expectedKind` hint
 * - kind is recording (recording rows have a different shape — RecordingMeta
 *   doesn't carry the title; callers must read entityMeta.get(rec.entityId)
 *   for the linked-doc title)
 *
 * Reads via the kind-agnostic `useEntityRow` accessor so adding a new
 * entity kind in the future doesn't require updating this hook.
 */
export function useEntityById(
  id: string | null | undefined,
  expectedKind?: EntityKind,
): ResolvedEntity | null {
  const row = useEntityRow(id);
  if (!id || !row) return null;

  // If caller provided an explicit hint, the row's actual kind must match.
  if (expectedKind !== undefined && row.kind !== expectedKind) {
    return null;
  }

  switch (row.kind) {
    case 'document':
      return { id, kind: 'document', title: row.title, snippet: row.snippet || undefined };
    case 'collection':
      return { id, kind: 'collection', title: row.title };
    case 'canvas':
      return { id, kind: 'canvas', title: row.title, snippet: row.snippet || undefined };
    case 'space':
      return { id, kind: 'space', title: row.title };
    default:
      return null;
  }
}
